import { api } from '@/lib/api/client';
import {
  autocompleteClubsApi,
  createClubClaimApi,
  createMembershipRequestApi,
  getClubApi,
  getClubsApi,
  getMyMembershipRequestApi,
  updateClubContactApi,
} from '@api-client/clubs';
import { deleteFileApi, uploadFileApi } from '@api-client/files';
import type { ClubAffiliationRequestStatus, ClubMembershipRequestStatus, ClubPlan } from '@shared/enums/domain.enum';
import type {
  ClubAdminClaimRequest,
  ClubAffiliationRequest,
  ClubAnalyticsResponse,
  ClubHistoryEventType,
  ClubHistoryResponse,
  ClubMembershipRequest,
  CreateClubPayload,
  UpdateClubPayload,
} from '@shared/types/entities';

import { type PaginatedResponse, buildQuery } from './types';

export {
  autocompleteClubsApi,
  createClubClaimApi,
  getClubsApi,
  getClubApi,
  createMembershipRequestApi,
  getMyMembershipRequestApi,
  updateClubContactApi,
};
export type {
  Club,
  ClubAutocompleteItem,
  ClubMembershipRequest,
  GetClubsParams,
  CreateClubPayload,
  UpdateClubPayload,
} from '@api-client/clubs';

export async function createClubApi(payload: CreateClubPayload): Promise<import('@shared/types/entities').Club> {
  return api.post<import('@shared/types/entities').Club>('/clubs', payload);
}

export async function updateClubApi(
  id: number,
  payload: UpdateClubPayload,
): Promise<import('@shared/types/entities').Club> {
  return api.patch<import('@shared/types/entities').Club>(`/clubs/${id}`, payload);
}

/** Mise à jour administrative du statut/plan d'un club — SUPER_ADMIN. */
export async function adminUpdateClubStatusApi(
  id: number,
  payload: { isActive?: boolean; clubPlan?: ClubPlan },
): Promise<import('@shared/types/entities').Club> {
  return api.patch<import('@shared/types/entities').Club>(`/clubs/${id}/admin-status`, payload);
}

export async function uploadClubLogoApi(
  clubId: number,
  file: File,
): Promise<{ id: number; key: string; mimeType: string }> {
  return uploadFileApi({ usage: 'CLUB_LOGO', file, extraFields: { clubId: String(clubId) } });
}

export async function uploadClubCoverApi(
  clubId: number,
  file: File,
): Promise<{ id: number; key: string; mimeType: string }> {
  return uploadFileApi({ usage: 'CLUB_COVER', file, extraFields: { clubId: String(clubId) } });
}

export async function deleteClubCoverApi(fileId: number): Promise<void> {
  return deleteFileApi(fileId);
}

// ─── Affiliation Requests ─────────────────────────────────────

export interface GetAffiliationRequestsParams {
  organizationId?: number;
  clubId?: number;
  status?: ClubAffiliationRequestStatus;
  page?: number;
  limit?: number;
}

export async function getAffiliationRequestsApi(
  params?: GetAffiliationRequestsParams,
): Promise<PaginatedResponse<ClubAffiliationRequest>> {
  return api.get(`/clubs/affiliation-requests${buildQuery(params as Record<string, unknown>)}`);
}

export async function updateAffiliationRequestApi(
  requestId: number,
  data: {
    status: ClubAffiliationRequestStatus.ACCEPTED | ClubAffiliationRequestStatus.REJECTED;
    rejectedReason?: string;
  },
): Promise<ClubAffiliationRequest> {
  return api.patch(`/clubs/affiliation-requests/${requestId}`, data);
}

export async function createAffiliationRequestApi(
  clubId: number,
  data: { organizationId: number; message?: string },
): Promise<ClubAffiliationRequest> {
  return api.post(`/clubs/${clubId}/affiliation-request`, data);
}

// ─── Membership Requests ───────────────────────────────────────

export interface GetMembershipRequestsParams {
  status?: ClubMembershipRequestStatus;
  page?: number;
  limit?: number;
}

export async function getMembershipRequestsApi(
  clubId: number,
  params?: GetMembershipRequestsParams,
): Promise<{
  data: ClubMembershipRequest[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}> {
  return api.get(`/clubs/${clubId}/membership-requests${buildQuery(params as Record<string, unknown>)}`);
}

export async function updateMembershipRequestApi(
  clubId: number,
  requestId: number,
  data: {
    status: ClubMembershipRequestStatus.ACCEPTED | ClubMembershipRequestStatus.REJECTED;
    rejectedReason?: string;
  },
): Promise<ClubMembershipRequest> {
  return api.patch(`/clubs/${clubId}/membership-requests/${requestId}`, data);
}

export async function removeMemberApi(clubId: number, userId: number): Promise<{ message: string }> {
  return api.delete(`/clubs/${clubId}/members/${userId}`);
}

// ─── History ───────────────────────────────────────────────────

export interface GetClubHistoryParams {
  type?: ClubHistoryEventType;
  page?: number;
  limit?: number;
}

export async function getClubHistoryApi(clubId: number, params?: GetClubHistoryParams): Promise<ClubHistoryResponse> {
  return api.get(`/clubs/${clubId}/history${buildQuery(params as Record<string, unknown>)}`);
}

export async function getClubAnalyticsApi(clubId: number): Promise<ClubAnalyticsResponse> {
  return api.get(`/clubs/${clubId}/analytics`);
}

// ─── Admin Club Claims ─────────────────────────────────────────

/**
 * Enriched claim item returned by GET /v1/clubs/claims/pending.
 * Extends ClubAdminClaimRequest with server-joined club, requester, and voteCount.
 */
export interface PendingClaimItem extends ClubAdminClaimRequest {
  club: { id: number; name: string; alias: string | null; city: string };
  requester: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    licenseNumber: string | null;
  };
  voteCount: number;
}

export async function getPendingClaimsApi(): Promise<PendingClaimItem[]> {
  return api.get('/clubs/claims/pending');
}

export async function getClubPendingClaimsApi(clubId: number): Promise<PendingClaimItem[]> {
  return api.get(`/clubs/${clubId}/claims/pending`);
}

export async function approveClaimApi(claimId: number): Promise<{ message: string }> {
  return api.post(`/clubs/claims/${claimId}/approve`, {});
}

export async function rejectClaimApi(claimId: number, reason?: string): Promise<{ message: string }> {
  return api.post(`/clubs/claims/${claimId}/reject`, { reason });
}

// ─── Stripe Subscription (Club Pro) ──────────────────────────

export async function clubStripeSetupIntentApi(clubId: number): Promise<{ clientSecret: string }> {
  return api.post(`/clubs/${clubId}/stripe/setup-intent`, {});
}

export async function clubStripeSubscribeApi(
  clubId: number,
  interval: 'monthly' | 'annual',
  paymentMethodId?: string,
): Promise<{ subscriptionId: string; status: string; clientSecret?: string }> {
  return api.post(`/clubs/${clubId}/stripe/subscribe`, { interval, ...(paymentMethodId && { paymentMethodId }) });
}

export async function clubStripeBillingPortalApi(clubId: number): Promise<{ portalUrl: string }> {
  return api.post(`/clubs/${clubId}/stripe/billing-portal`, {});
}
